import { useState } from 'react';
import type { ArrowProfile, ArrowPointSpec } from '../types';
import { ARROW_LIBRARY } from '../../lib/arrowLibrary';

interface ArrowProfileFormProps {
  profile: ArrowProfile;
  mode: 'create' | 'edit';
  onSave: (p: ArrowProfile) => void;
  onCancel: () => void;
}

const POINT_TYPES: ArrowPointSpec['type'][] = [
  'COC fixed',
  'single-bevel',
  'fixed mech',
  'hybrid mech',
  'full mech',
];

export function ArrowProfileForm({ profile, mode, onSave, onCancel }: ArrowProfileFormProps) {
  const [draft, setDraft] = useState<ArrowProfile>(profile);

  function update<K extends keyof ArrowProfile>(section: K, patch: Partial<ArrowProfile[K]>) {
    setDraft({ ...draft, [section]: { ...(draft[section] as object), ...patch } });
  }

  const arrowBrands = Array.from(new Set(ARROW_LIBRARY.map((a) => a.brand))).sort();
  const arrowModelsForBrand = draft.shaft.brand
    ? ARROW_LIBRARY.filter((a) => a.brand === draft.shaft.brand)
    : [];

  return (
    <form
      className="profile-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!draft.name.trim()) return;
        onSave(draft);
      }}
    >
      <h2>{mode === 'create' ? 'Add an arrow' : `Edit ${profile.name}`}</h2>

      <section className="form-section">
        <h3>Profile</h3>
        <label>
          Name
          <input
            type="text"
            value={draft.name}
            placeholder="e.g. Western elk 510gr"
            required
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </label>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={draft.isActive}
            onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
          />
          Mark as active arrow (defaults across the Forge)
        </label>
      </section>

      {/* Build flow order: shaft → nock → vanes → insert → point */}

      <section className="form-section">
        <h3>1. Shaft</h3>
        <div className="form-grid">
          <label>
            Brand
            <select
              value={draft.shaft.brand ?? ''}
              onChange={(e) => update('shaft', { brand: e.target.value, model: '' })}
            >
              <option value="">— Select —</option>
              {arrowBrands.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </label>
          <label>
            Model
            <select
              value={draft.shaft.model ?? ''}
              onChange={(e) => update('shaft', { model: e.target.value })}
              disabled={!draft.shaft.brand}
            >
              <option value="">— Select —</option>
              {arrowModelsForBrand.map((a) => (
                <option key={a.model} value={a.model}>{a.model}</option>
              ))}
            </select>
          </label>
          <label>
            Spine
            <input
              type="number"
              value={draft.shaft.spine}
              step={10}
              min={150}
              max={1000}
              onChange={(e) => update('shaft', { spine: Number(e.target.value) })}
            />
          </label>
          <label>
            Length (in)
            <input
              type="number"
              value={draft.shaft.lengthIn}
              step={0.25}
              min={20}
              max={34}
              onChange={(e) => update('shaft', { lengthIn: Number(e.target.value) })}
            />
          </label>
        </div>
      </section>

      <section className="form-section">
        <h3>2. Nock</h3>
        <div className="form-grid">
          <label>
            Type
            <input
              type="text"
              value={draft.nock.type ?? ''}
              placeholder="Standard / Fire Nock / pin"
              onChange={(e) => update('nock', { type: e.target.value })}
            />
          </label>
          <label>
            Weight (gr)
            <input
              type="number"
              value={draft.nock.weightGr}
              step={1}
              min={5}
              max={40}
              onChange={(e) => update('nock', { weightGr: Number(e.target.value) })}
            />
          </label>
        </div>
      </section>

      <section className="form-section">
        <h3>3. Vanes</h3>
        <div className="form-grid">
          <label>
            Brand
            <input
              type="text"
              value={draft.vanes.brand ?? ''}
              placeholder="DCA / AAE / Bohning / etc."
              onChange={(e) => update('vanes', { brand: e.target.value })}
            />
          </label>
          <label>
            Model
            <input
              type="text"
              value={draft.vanes.model ?? ''}
              placeholder="Sabre / Talon / Max Stealth"
              onChange={(e) => update('vanes', { model: e.target.value })}
            />
          </label>
          <label>
            Count
            <input
              type="number"
              value={draft.vanes.count}
              min={2}
              max={4}
              onChange={(e) => update('vanes', { count: Number(e.target.value) })}
            />
          </label>
          <label>
            Offset (°)
            <input
              type="number"
              value={draft.vanes.offsetDeg}
              step={0.5}
              min={0}
              max={6}
              onChange={(e) => update('vanes', { offsetDeg: Number(e.target.value) })}
            />
          </label>
        </div>
      </section>

      <section className="form-section">
        <h3>4. Insert / Collar</h3>
        <div className="form-grid">
          <label>
            Brand
            <input
              type="text"
              value={draft.insert.brand ?? ''}
              placeholder="Iron Will / CTI / Easton HIT / etc."
              onChange={(e) => update('insert', { brand: e.target.value })}
            />
          </label>
          <label>
            Weight (gr)
            <input
              type="number"
              value={draft.insert.weightGr}
              step={5}
              min={0}
              max={150}
              onChange={(e) => update('insert', { weightGr: Number(e.target.value) })}
            />
          </label>
        </div>
      </section>

      <section className="form-section">
        <h3>5. Point</h3>
        <div className="form-grid">
          <label>
            Brand
            <input
              type="text"
              value={draft.point.brand ?? ''}
              placeholder="Iron Will / Grim Reaper / Sevr / etc."
              onChange={(e) => update('point', { brand: e.target.value })}
            />
          </label>
          <label>
            Model
            <input
              type="text"
              value={draft.point.model ?? ''}
              placeholder="V100 / Carnifour / SK2 / etc."
              onChange={(e) => update('point', { model: e.target.value })}
            />
          </label>
          <label>
            Type
            <select
              value={draft.point.type}
              onChange={(e) => update('point', { type: e.target.value as ArrowPointSpec['type'] })}
            >
              {POINT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>
          <label>
            Weight (gr)
            <input
              type="number"
              value={draft.point.weightGr}
              step={25}
              min={75}
              max={300}
              onChange={(e) => update('point', { weightGr: Number(e.target.value) })}
            />
          </label>
        </div>
      </section>

      <div className="form-actions">
        <button type="submit" className="primary-cta">
          {mode === 'create' ? 'Save arrow' : 'Save changes'}
        </button>
        <button type="button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
